import { DateTime } from "luxon";
import { useCallback, useContext, useEffect, useState } from "react";
import StoreContext from "../context/StoreContext";
import { Tooltip } from "@mui/material";
import TimePickerModal from "./TimePickerModal";
import ChangeTimeModal from "./ChangeTimeModal";

const PromisedTime = ({ rowData }) => {
  const { time_zone } = useContext(StoreContext);
  const [openChangeTimeModal, setOpenChangeTimeModal] = useState(false);
  const [openTimePicker, setOpenTimePicker] = useState(false);
  const [promisedTimeButton, setPromisedTimeButton] = useState(false);
  const [alert, setAlert] = useState(false);
  const promisedTime = DateTime.fromISO(rowData.customer_eta).setZone(
    time_zone
  );
  const displayPromisedTime = promisedTime.toFormat("MM-dd hh:mm a");
  const isWOFinished = rowData.cf_order_status === "C" || (!!rowData.SmsData?.vehicleReadyMessage.length && rowData.SmsData?.vehicleReadyMessage[0]?.Status)
  const ignoredAlert = rowData.promised_time_details?.ignore_update;
  const hasPromisedTime = !!rowData.SmsData?.promisedTimeMessage && rowData.SmsData?.promisedTimeMessage[0]?.Status;

  const checkTime = useCallback(
    (call, interval = null) => {
      const now = DateTime.now().setZone(time_zone);
      setAlert(promisedTime.minus({ minutes: 30 }) <= now);
      setPromisedTimeButton(now < promisedTime);
      
      if (now > promisedTime) {
        setAlert("expired");
        if (interval) {
          clearInterval(interval);
        }
      }
    },
    [promisedTime, rowData.SmsData?.promisedTimeMessageUpdate, time_zone]
  );

  /**
   *
   * @returns time until next minute in milliseconds
   */
  const millisecondsUntilNextMinute = () => {
    const now = DateTime.now();
    const millisecondsUntilNextMinute = 60000 - (now.toMillis() % 60000);
    return millisecondsUntilNextMinute;
  };

  useEffect(() => {
    // TODO: Move millisecondsUntilNextMinute to parent component. No need for each row to run it.
    // Ideally, move all of the promisedtime timecheck to parent component and pass prop to this component.

    if(ignoredAlert || isWOFinished || !hasPromisedTime){
      setPromisedTimeButton(false);
      setAlert(false);
      return;
    }

    // render promise time cell on page load.
    checkTime("outer");

    // Start the interval at the top of the minute.
    const timeoutId = setTimeout(() => {
      // Call checkTime every minute
      const intervalId = setInterval(() => {
        checkTime("inner", intervalId);
      }, 60000);
      return () => clearInterval(intervalId);
    }, millisecondsUntilNextMinute());

    return () => clearTimeout(timeoutId);

  }, [checkTime, ignoredAlert, isWOFinished, hasPromisedTime]);

  const handlePromisedTimeClick = () => {
    setOpenChangeTimeModal(true);
  };

  return (
    <>
      <Tooltip
        title={alert === "expired" && "Promise Time Expired: Unable to Update"}
      >
        <button
          className={`
            ${promisedTimeButton && "bg-white hover:bg-gray-600 hover:text-white border border-gray-600 rounded-full py-1 px-2 text-sm"}
            ${(alert && promisedTimeButton) && "bg-yellow-500 hover:bg-yellow-600 text-white border border-yellow-600 rounded-full py-1 px-2 text-sm"}
            ${alert && "bg-red-600 h-8 w-max text-white rounded-full py-1 px-2 text-sm"}
          `}
          style={{cursor: `${promisedTimeButton ? 'pointer' : 'text'}`}}
          onClick={
            (promisedTimeButton)
              ? handlePromisedTimeClick
              : null
          }
        >
          {displayPromisedTime}
        </button>
      </Tooltip>

      {openChangeTimeModal && (
        <ChangeTimeModal
          open={openChangeTimeModal}
          close={() => setOpenChangeTimeModal(false)}
          handleChangeTime={() => setOpenTimePicker(true)}
          time={promisedTime}
          workOrder={rowData}
          setAlert={setAlert}
          setPromisedTimeButton={setPromisedTimeButton}
        />
      )}
      {openTimePicker && (
        <TimePickerModal
          open={openTimePicker}
          close={() => setOpenTimePicker(false)}
          previousTime={promisedTime}
          maxTime={DateTime.utc().set({ hour: 23, minute: 45 })}
          closeAll={() => setOpenChangeTimeModal(false)}
          workOrder={rowData}
          setAlert={setAlert}
          setPromisedTimeButton={setPromisedTimeButton}
        />
      )}
    </>
  );
};

export default PromisedTime;
